// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { xParserOptions } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";

interface Props {
    label: string;
    tooltipText?: string;
    value: string | number | boolean;
    options?: any;
    optionType?: "string" | "object" | "hose";
    onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    labelIsHorizontal?: boolean;
    extendedStyles?: JettingAppDropDownStyles;
    readOnly?: boolean;
    currentUnit?: string;
}

export interface JettingAppDropDownStyles {
    wrapper?: InjectableCss;
    dropDown?: SelectPresentationProps;
    tooltip?: TooltipPresentationProps;
    unit?: InjectableCss;
}

export const jettingAppDropDownStyles: JettingAppDropDownStyles = {};

const JettingAppDropDown = ({
    extendedStyles,
    label,
    value,
    tooltipText,
    onChange,
    labelIsHorizontal = false,
    options,
    optionType = "object",
    readOnly,
    currentUnit,
}: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppDropDownStyles, extendedStyles));
    const [dropdownValue, setDropdownValue] = useState(value);

    useEffect(() => {
        if (dropdownValue === value) {
            return;
        }
        setDropdownValue(`${value}`);
    }, [value, dropdownValue]);

    const textLabel = tooltipText ? (
        <>
            {label}
            <Tooltip {...styles.tooltip} passAsChildren={true}>
                <Typography>{parse(tooltipText, xParserOptions)}</Typography>
            </Tooltip>
        </>
    ) : (
        label
    );

    const onDropDownChange: React.ChangeEventHandler<HTMLSelectElement> = event => {
        const value = event.currentTarget.value;
        setDropdownValue(value);
        onChange && onChange(event);
    };

    // display as disabled textfield if we have only one option
    if (options.length === 1) {
        return (
            <StyledWrapper {...styles.wrapper} {...{ labelIsHorizontal, currentUnit }}>
                <TextField
                    disabled
                    label={textLabel}
                    value={
                        optionType === "hose"
                            ? options[0]?.hoseDescription
                            : optionType === "object"
                            ? options[0]?.label
                            : options[0]
                    }
                    data-test-selector="jetting-field"
                />
                {currentUnit && (
                    <StyledWrapper {...styles.unit} className="single-value-unit" translate="no">
                        {currentUnit}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper} {...{ labelIsHorizontal, currentUnit }}>
            <Select
                disabled={!options?.length || readOnly}
                {...styles.dropDown}
                label={textLabel}
                value={dropdownValue?.toString() || ""}
                onChange={onDropDownChange}
                translate={textLabel ? "yes" : "no"}
            >
                <option value="">{label ? `${translate("Select")} ${label}` : `${translate("Select Option")}`}</option>
                {options?.map((o: any, index: number) => (
                    <option
                        key={index}
                        value={optionType === "hose" ? o.hoseID : optionType === "object" ? o.value : o}
                    >
                        {optionType === "hose" ? o.hoseDescription : optionType === "object" ? o.label : o}
                    </option>
                ))}
            </Select>
            {currentUnit && (
                <StyledWrapper {...styles.unit} translate="no">
                    {currentUnit}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
export default JettingAppDropDown;
